import React, { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeft, Sparkles, Crown, Gem, Zap, Target, BarChart3, FileText, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const AppTour = ({ run, onFinish, userTier = 'free', tourType = 'full', onOpenSidebar, onDisable }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isConfirmingDisable, setIsConfirmingDisable] = useState(false);
  const [highlightedElement, setHighlightedElement] = useState(null);
  const [spotlightRect, setSpotlightRect] = useState(null);

  const isPro = userTier === 'pro';
  const isPremiumOrPro = userTier === 'premium' || userTier === 'pro';

  // Steps básicos para todos los usuarios
  const baseSteps = [
    {
      title: '¡Bienvenido a Portal de Gastos! 🎉',
      description: 'Te mostraremos cómo usar la aplicación en unos simples pasos. Prepárate para tomar control total de tus finanzas.',
      position: 'center',
      icon: Sparkles,
      gradient: 'from-blue-500 to-indigo-600',
    },
    {
      title: 'Dashboard 📊',
      description: 'Tu centro de control financiero. Aquí verás un resumen completo: balance total, ingresos, gastos y ahorros en tiempo real.',
      target: 'nav a[href="/dashboard"]',
      icon: BarChart3,
      gradient: 'from-green-500 to-emerald-600',
    },
    {
      title: 'Transacciones 💳',
      description: 'Registra todas tus transacciones fácilmente: ingresos, gastos, ahorros e inversiones. Todo en un solo lugar.',
      target: 'nav a[href="/transactions"]',
      icon: Zap,
      gradient: 'from-purple-500 to-pink-600',
    },
    {
      title: 'Cuentas Bancarias 🏦',
      description: 'Gestiona tus cuentas bancarias, tarjetas de crédito, efectivo y billeteras digitales. Visualiza balances y transacciones por cuenta.',
      target: 'nav a[href="/accounts"]',
      icon: Zap,
      gradient: 'from-blue-500 to-indigo-600',
    },
    {
      title: 'Recordatorios de Pago 🔔',
      description: 'Nunca olvides un pago importante. Crea recordatorios para tus facturas recurrentes: luz, agua, renta, tarjetas y más.',
      target: 'nav a[href="/payment-reminders"]',
      icon: Target,
      gradient: 'from-red-500 to-rose-600',
    },
    {
      title: 'Categorías 🏷️',
      description: 'Organiza tus finanzas con categorías personalizadas. Crea, edita y visualiza tus gastos de manera estructurada.',
      target: 'nav a[href="/categories"]',
      icon: Target,
      gradient: 'from-amber-500 to-orange-600',
    },
    {
      title: 'Estadísticas 📈',
      description: 'Visualiza gráficos interactivos de tus finanzas: distribución por categorías, tendencias mensuales y más.',
      target: 'nav a[href="/statistics"]',
      icon: BarChart3,
      gradient: 'from-blue-500 to-cyan-600',
    },
  ];

  // Steps adicionales para usuarios Premium/Pro
  const premiumSteps = [
    {
      title: '👑 ¡Funciones Premium Desbloqueadas!',
      description: tourType === 'full' 
        ? 'Como usuario Premium, tienes acceso a herramientas avanzadas para mejorar tu gestión financiera.'
        : '¡Felicidades por actualizar a Premium! Te mostramos las nuevas funciones que ahora tienes disponibles.',
      position: 'center',
      icon: Crown,
      gradient: 'from-amber-500 to-yellow-600',
      badge: tourType !== 'full' ? '¡NUEVO!' : 'Premium',
    },
    {
      title: '💬 IA Financiera - Chat Inteligente',
      description: 'Haz clic en este botón para abrir el chatbot de IA. Te dará consejos personalizados sobre tus finanzas basándose en tus datos reales.',
      target: 'button[aria-label="Abrir chat IA"]',
      position: 'top',
      icon: Crown,
      gradient: 'from-amber-500 to-yellow-600',
      badge: 'Premium',
    },
    {
      title: '🎯 Objetivos Financieros',
      description: 'Crea y da seguimiento a tus metas financieras. Ahorra para ese viaje, auto o cualquier objetivo que tengas.',
      target: 'nav a[href="/goals"]',
      icon: Target,
      gradient: 'from-amber-500 to-yellow-600',
      badge: 'Premium',
    },
  ];

  // Steps exclusivos para usuarios Pro
  const proSteps = [
    {
      title: '💎 ¡Bienvenido al Plan Pro!',
      description: tourType === 'full'
        ? 'Tienes acceso a las herramientas más avanzadas de Portal de Gastos.'
        : '¡Has desbloqueado el máximo potencial! Te mostramos las exclusivas funciones Pro.',
      position: 'center',
      icon: Gem,
      gradient: 'from-purple-500 to-pink-600',
      badge: tourType !== 'full' ? '¡NUEVO!' : 'Pro',
    },
    {
      title: '📊 Análisis Avanzados',
      description: 'Accede a la regla 50/30/20, recomendaciones personalizadas y análisis profundos de tus finanzas.',
      target: 'nav a[href="/advanced-analytics"]',
      icon: Gem,
      gradient: 'from-purple-500 to-pink-600',
      badge: 'Pro',
    },
    {
      title: '📄 Exportar Reportes',
      description: 'En la página de Transacciones encontrarás botones para descargar tus datos en PDF, Excel o CSV. Perfecto para impuestos o análisis externos.',
      target: 'nav a[href="/transactions"]',
      icon: FileText,
      gradient: 'from-purple-500 to-pink-600',
      badge: 'Pro',
    },
    {
      title: '🚀 Soporte Prioritario',
      description: 'Obtén respuestas a tus dudas en menos de 24 horas. Tu éxito financiero es nuestra prioridad.',
      target: 'nav a[href="/contact"]',
      icon: MessageSquare,
      gradient: 'from-purple-500 to-pink-600',
      badge: 'Pro',
    },
  ];

  // Combinar steps según el tourType
  let steps = [];
  
  if (tourType === 'full') {
    steps = [...baseSteps];
    if (isPremiumOrPro) {
      steps = [...steps, ...premiumSteps];
    }
    if (isPro) {
      steps = [...steps, ...proSteps];
    }
  } else if (tourType === 'premium-only') {
    steps = [...premiumSteps];
  } else if (tourType === 'pro-only') {
    steps = [...proSteps];
  } else if (tourType === 'premium-and-pro') {
    steps = [...premiumSteps, ...proSteps];
  }

  // Step final
  steps.push({
    title: tourType === 'full'
      ? (isPro ? '¡Aprovecha todas las funciones Pro! 💎' : isPremiumOrPro ? '¡Todo listo! 👑' : '¡Todo listo! 🚀')
      : tourType === 'pro-only'
      ? '¡Disfruta de tus nuevas funciones Pro! 💎'
      : tourType === 'premium-only'
      ? '¡Disfruta de tus nuevas funciones Premium! 👑'
      : '¡Explora tus nuevas funciones! ✨',
    description: tourType === 'full'
      ? (isPro 
          ? 'Tienes acceso a todas las herramientas premium y pro. ¡Maximiza tu potencial financiero!'
          : isPremiumOrPro
          ? 'Disfruta de todas las funciones premium. ¿Quieres más? Actualiza a Pro para análisis avanzados y exportación de datos.'
          : '¡Comienza a gestionar tus finanzas de manera inteligente! ¿Quieres más funciones? Actualiza a Premium o Pro.')
      : 'Ya conoces todas tus nuevas funciones. ¡Empieza a usarlas y lleva tus finanzas al siguiente nivel!',
    position: 'center',
    icon: tourType.includes('pro') ? Gem : tourType.includes('premium') ? Crown : Sparkles,
    gradient: tourType.includes('pro') ? 'from-purple-500 to-pink-600' : tourType.includes('premium') ? 'from-amber-500 to-yellow-600' : 'from-blue-500 to-indigo-600',
  });

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  useEffect(() => {
    if (!run) {
      setCurrentStep(0);
      setHighlightedElement(null);
      setSpotlightRect(null);
    }
  }, [run]);

  useEffect(() => {
    if (run && currentStepData?.target) {
      setTimeout(() => {
        const element = document.querySelector(currentStepData.target);
        if (element) {
          setHighlightedElement(element);
          
          // Get element position
          const rect = element.getBoundingClientRect();
          setSpotlightRect(rect);
          
          // Si el elemento está en el sidebar, abrir sidebar en móvil
          const sidebar = document.querySelector('aside');
          if (sidebar && sidebar.contains(element)) {
            if (onOpenSidebar) {
              onOpenSidebar();
            }
          }
          
          // Scroll to element
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          console.warn(`Tour: Element not found for selector: ${currentStepData.target}`);
        }
      }, 100);
    } else {
      setHighlightedElement(null);
      setSpotlightRect(null);
    }
  }, [currentStep, run, currentStepData, onOpenSidebar]);

  // Update spotlight position on scroll/resize
  useEffect(() => {
    if (!highlightedElement) return;

    const updatePosition = () => {
      const rect = highlightedElement.getBoundingClientRect();
      setSpotlightRect(rect);
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [highlightedElement]);

  useEffect(() => {
    if (run) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [currentStep, run]);

  if (!run) return null;

  const handleNext = () => {
    if (isLastStep) {
      onFinish({ status: 'finished' });
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onFinish({ status: 'skipped' });
  };

  const Icon = currentStepData.icon || Sparkles;

  // Determinar posición del card según el elemento destacado
  let cardPosition = currentStepData.position === 'center' 
    ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
    : 'bottom-4 left-1/2 -translate-x-1/2 sm:bottom-8';

  // Si el elemento está en la parte inferior, posicionar card arriba
  if (spotlightRect && currentStepData.position === 'top') {
    cardPosition = 'top-20 left-1/2 -translate-x-1/2';
  } else if (spotlightRect && spotlightRect.bottom > window.innerHeight - 150) {
    cardPosition = 'top-20 left-1/2 -translate-x-1/2';
  }

  return (
    <>
      <style>
        {`
          @keyframes pulse-border {
            0%, 100% {
              box-shadow: 0 0 0 4px rgba(255, 255, 255, 1), 0 0 30px 5px rgba(99, 102, 241, 0.5);
              border-color: rgba(99, 102, 241, 0.9);
            }
            50% {
              box-shadow: 0 0 0 6px rgba(255, 255, 255, 1), 0 0 50px 15px rgba(99, 102, 241, 0.7);
              border-color: rgba(99, 102, 241, 1);
            }
          }
        `}
      </style>

      {/* Overlay con cutout para el elemento destacado */}
      {spotlightRect ? (
        <svg
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 9998
          }}
        >
          <defs>
            <mask id="spotlight-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              <rect
                x={spotlightRect.left - 8}
                y={spotlightRect.top - 8}
                width={spotlightRect.width + 16}
                height={spotlightRect.height + 16}
                rx="12"
                fill="black"
              />
            </mask>
            <filter id="blur">
              <feGaussianBlur in="SourceGraphic" stdDeviation="2" />
            </filter>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.75)"
            mask="url(#spotlight-mask)"
            filter="url(#blur)"
          />
        </svg>
      ) : (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9998]"
        />
      )}

      {/* Overlay invisible para bloquear clicks - NO intercepta clicks, solo bloquea interacción */}
      <div 
        className="fixed inset-0 z-[9999]"
        style={{ pointerEvents: 'auto' }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      />

      {/* Border animado alrededor del elemento */}
      {spotlightRect && (
        <div
          style={{
            position: 'fixed',
            top: `${spotlightRect.top - 8}px`,
            left: `${spotlightRect.left - 8}px`,
            width: `${spotlightRect.width + 16}px`,
            height: `${spotlightRect.height + 16}px`,
            border: '4px solid rgba(99, 102, 241, 0.9)',
            borderRadius: '12px',
            pointerEvents: 'none',
            zIndex: 9999,
            animation: 'pulse-border 2s ease-in-out infinite'
          }}
        />
      )}

      {/* Tour Card */}
      <div
        className={`fixed z-[10000] bg-white rounded-2xl shadow-2xl w-full overflow-hidden transform transition-all duration-300 
          mx-2 sm:mx-4 max-w-[calc(100%-1rem)] sm:max-w-md
          ${isAnimating ? 'scale-95 opacity-0' : 'scale-100 opacity-100'} 
          ${cardPosition}`}
      >
        {/* Gradient Header */}
        <div className={`bg-gradient-to-r ${currentStepData.gradient} p-3 sm:p-6 text-white relative overflow-hidden`}>
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }} />
          </div>

          {/* Close Button */}
          <button
            onClick={handleSkip}
            className="absolute top-2 right-2 sm:top-4 sm:right-4 text-white/80 hover:text-white transition-colors z-10 p-1"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          {/* Icon animado */}
          <div className="relative z-10 flex items-center gap-2 mb-1 sm:mb-2">
            <div className="p-1.5 sm:p-3 bg-white/20 backdrop-blur-sm rounded-xl animate-bounce">
              <Icon className="w-4 h-4 sm:w-6 sm:h-6" />
            </div>
            {currentStepData.badge && (
              <span className="px-2 py-0.5 sm:px-3 sm:py-1 bg-white/30 backdrop-blur-sm rounded-full text-xs font-bold">
                {currentStepData.badge}
              </span>
            )}
          </div>

          <h3 className="text-lg sm:text-2xl font-bold relative z-10 pr-6">
            {currentStepData.title}
          </h3>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-6">
          {/* Progress Bar */}
          <div className="mb-3 sm:mb-6">
            <div className="flex gap-0.5 sm:gap-1.5">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-1 sm:h-2 flex-1 rounded-full transition-all duration-500 ${
                    index === currentStep
                      ? `bg-gradient-to-r ${currentStepData.gradient} shadow-lg`
                      : index < currentStep
                      ? 'bg-slate-400'
                      : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1 sm:mt-2 text-right font-medium">
              Paso {currentStep + 1} de {steps.length}
            </p>
          </div>

          <p className="text-slate-600 text-xs sm:text-base leading-relaxed mb-3 sm:mb-6">
            {currentStepData.description}
          </p>

          {/* Buttons */}
          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              {!isFirstStep && (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-1 px-2 sm:px-4 py-1.5 sm:py-2.5 text-xs sm:text-base text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all font-medium"
                >
                  <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                  Atrás
                </button>
              )}
              
              <div className="flex-1" />
              
              {!isLastStep && (
                <button
                  onClick={handleSkip}
                  className="px-2 sm:px-4 py-1.5 sm:py-2.5 text-xs sm:text-base text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all font-medium"
                >
                  Saltar
                </button>
              )}
              
              <button
                onClick={handleNext}
                className={`flex items-center gap-1 px-3 sm:px-6 py-1.5 sm:py-2.5 text-xs sm:text-base bg-gradient-to-r ${currentStepData.gradient} text-white rounded-lg hover:shadow-lg transition-all font-medium transform hover:scale-105`}
              >
                {isLastStep ? '¡Empezar!' : 'Siguiente'}
                {!isLastStep && <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />}
              </button>
            </div>
            
            {/* Botón para desactivar permanentemente */}
            {onDisable && (
              <button
                onClick={() => {
                  if (isConfirmingDisable) return;
                  setIsConfirmingDisable(true);
                  
                  toast.error('¿Seguro que quieres desactivar el tour permanentemente?', {
                    duration: 10000,
                    onDismiss: () => setIsConfirmingDisable(false),
                    onAutoClose: () => setIsConfirmingDisable(false),
                    action: {
                      label: 'Sí, desactivar',
                      onClick: () => {
                        onDisable();
                        setIsConfirmingDisable(false);
                        toast.success('Tour desactivado permanentemente');
                      }
                    },
                    cancel: {
                      label: 'Cancelar',
                      onClick: () => setIsConfirmingDisable(false)
                    }
                  });
                }}
                className="text-xs text-slate-500 hover:text-red-600 transition-colors text-center"
              >
                No volver a mostrar este tour
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AppTour;
